/**
 * <s-form-quiz> — Find your form quiz.
 *
 * One question per screen. Buttons talk to this element through Invoker
 * Commands (commandfor = this element's id):
 *   --start, --answer (button carries data-answer), --skip, --back, --edit, --restart
 *
 * Required child markup:
 *   [data-step="intro|recipient|form|finish|color|story|results"] — panels
 *   [data-quiz-progress] / [data-quiz-bar]  — progress text and bar per step
 *   [data-quiz-heading]                      — focus target per step (tabindex="-1")
 *   [data-quiz-results-list]                 — list the picks are copied into
 *   [data-quiz-empty], [data-quiz-shop-all], [data-quiz-gift]
 *   [data-quiz-pool] > [data-quiz-item]      — candidates rendered by Liquid
 *
 * Matching: recipient and form filter first (finish narrows A-rags), then
 * color +10 and chapter +20. Skips are neutral. In-stock pieces lead, then
 * score, then the collection's own order, then product ID.
 */
const FLOW = ['recipient', 'form', 'finish', 'color', 'story'];

class SFormQuiz extends HTMLElement {
  connectedCallback() {
    this.answers = {};
    this.labels = {};
    this.history = [];
    this.urls = this.#readUrls();
    this.pool = [...this.querySelectorAll('[data-quiz-pool] > [data-quiz-item]')];
    this.addEventListener('command', this.#onCommand);

    // After the email signup posts, the page reloads without the hash; bring the picks back.
    if (!location.hash.startsWith('#picks=') && this.querySelector('[data-quiz-return]')) {
      const saved = sessionStorage.getItem('form-quiz-picks');
      if (saved) history.replaceState(null, '', `${location.pathname}${location.search}#picks=${saved}`);
    }
    const restored = this.#restoreFromHash();
    this.#show(restored ? 'results' : 'intro', { focus: false });
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.#onCommand);
  }

  #onCommand = (event) => {
    const source = event.source;
    const step = source?.closest('[data-step]')?.dataset.step;
    switch (event.command) {
      case '--start':
        this.history = [];
        this.#go('recipient');
        break;
      case '--answer':
        this.answers[step] = source.dataset.answer;
        this.labels[step] = source.querySelector('[data-answer-label]')?.textContent.trim() || '';
        this.#markPressed(step);
        this.#go(this.#next(step));
        break;
      case '--skip':
        delete this.answers[step];
        delete this.labels[step];
        this.#markPressed(step);
        this.#go(this.#next(step));
        break;
      case '--back':
        this.#show(this.history.pop() || 'intro');
        break;
      case '--edit':
        this.history = [];
        this.#go('recipient');
        break;
      case '--restart':
        this.answers = {};
        this.labels = {};
        this.history = [];
        FLOW.forEach((name) => this.#markPressed(name));
        history.replaceState(null, '', location.pathname + location.search);
        this.#show('intro');
        break;
    }
  };

  #next(step) {
    const { recipient, form } = this.answers;
    if (step === 'recipient') return recipient === 'child' || recipient === 'matching' ? 'color' : 'form';
    if (step === 'form') return form === 'arag' ? 'finish' : 'color';
    if (step === 'finish') return 'color';
    if (step === 'color') return 'story';
    return 'results';
  }

  #path() {
    const { recipient, form } = this.answers;
    const kidsOrSet = recipient === 'child' || recipient === 'matching';
    return FLOW.filter((name) => {
      if (name === 'form') return !kidsOrSet;
      if (name === 'finish') return !kidsOrSet && form === 'arag';
      return true;
    });
  }

  #go(step) {
    const current = this.querySelector('[data-step]:not([hidden])')?.dataset.step;
    if (current && current !== step) this.history.push(current);
    this.#show(step);
  }

  #show(step, { focus = true } = {}) {
    if (step === 'results') this.#renderResults();
    for (const panel of this.querySelectorAll('[data-step]')) {
      panel.hidden = panel.dataset.step !== step;
    }
    const panel = this.querySelector(`[data-step="${step}"]`);
    if (!panel) return;

    const path = this.#path();
    const index = path.indexOf(step);
    if (index > -1) {
      const text = (this.dataset.progressTemplate || '')
        .replace('__C__', String(index + 1))
        .replace('__T__', String(path.length));
      panel.querySelectorAll('[data-quiz-progress]').forEach((el) => (el.textContent = text));
      panel.querySelectorAll('[data-quiz-bar]').forEach((el) => {
        el.style.width = `${((index + 1) / path.length) * 100}%`;
      });
    }

    if (focus) {
      panel.querySelector('[data-quiz-heading]')?.focus({ preventScroll: true });
      this.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  #markPressed(step) {
    const panel = this.querySelector(`[data-step="${step}"]`);
    if (!panel) return;
    for (const button of panel.querySelectorAll('[data-answer]')) {
      button.setAttribute('aria-pressed', String(button.dataset.answer === this.answers[step]));
    }
  }

  #candidates() {
    const { recipient, form, finish } = this.answers;
    const has = (item, key) => item.dataset.forms.split(' ').includes(key);
    if (recipient === 'child') return this.pool.filter((item) => has(item, 'kids'));
    if (recipient === 'matching') return this.pool.filter((item) => has(item, 'matching'));

    const adult = this.pool.filter((item) =>
      item.dataset.forms.split(' ').some((key) => key !== 'kids' && key !== 'matching')
    );
    if (!form || form === 'everything') return adult;
    if (form === 'arag') {
      const key = finish === 'velvet' || finish === 'silky' ? finish : 'arag';
      return adult.filter((item) => has(item, key));
    }
    return adult.filter((item) => has(item, form));
  }

  #renderResults() {
    const { color, story, recipient, form, finish } = this.answers;
    const scored = this.#candidates().map((item) => {
      let score = 0;
      const why = [];
      if (this.labels.form && form !== 'everything') why.push(this.labels.form);
      if (this.labels.finish && (finish === 'velvet' || finish === 'silky')) why.push(this.labels.finish);
      if (color && color !== 'any' && item.dataset.colors.split(' ').includes(color)) {
        score += 10;
        why.push(this.labels.color);
      }
      if (story && item.dataset.chapters.split(' ').includes(story)) {
        score += 20;
        why.push(this.labels.story);
      }
      return { item, score, why, available: item.dataset.available === 'true' };
    });

    scored.sort(
      (a, b) =>
        Number(b.available) - Number(a.available) ||
        b.score - a.score ||
        Number(a.item.dataset.rank) - Number(b.item.dataset.rank) ||
        Number(a.item.dataset.id) - Number(b.item.dataset.id)
    );

    const list = this.querySelector('[data-quiz-results-list]');
    const empty = this.querySelector('[data-quiz-empty]');
    list.replaceChildren();
    scored.slice(0, 3).forEach(({ item, why }, index) => {
      const clone = item.cloneNode(true);
      clone.removeAttribute('data-quiz-item');
      if (index === 0) {
        clone.querySelector('[data-quiz-top]')?.removeAttribute('hidden');
      }
      const whyEl = clone.querySelector('[data-quiz-why]');
      if (whyEl && why.length) {
        whyEl.querySelector('[data-quiz-why-text]').textContent = why.filter(Boolean).join(' · ');
        whyEl.hidden = false;
      }
      list.append(clone);
    });
    if (empty) empty.hidden = scored.length > 0;

    const shopKey =
      recipient === 'child' ? 'kids' :
      recipient === 'matching' ? 'matching' :
      form === 'arag' && (finish === 'velvet' || finish === 'silky') ? finish :
      form || 'everything';
    const shopAll = this.querySelector('[data-quiz-shop-all]');
    if (shopAll) shopAll.href = this.urls[shopKey] || this.urls.everything || shopAll.href;

    const gift = this.querySelector('[data-quiz-gift]');
    if (gift) {
      gift.hidden = !this.urls.gift;
      if (this.urls.gift) gift.href = this.urls.gift;
    }

    const code = FLOW.map((name) => this.answers[name] || '-').join('.');
    history.replaceState(null, '', `${location.pathname}${location.search}#picks=${code}`);
    try {
      sessionStorage.setItem('form-quiz-picks', code);
    } catch {}
  }

  #restoreFromHash() {
    const match = location.hash.match(/^#picks=([\w.-]+)$/);
    if (!match) return false;
    const values = match[1].split('.');
    FLOW.forEach((name, i) => {
      const value = values[i];
      if (!value || value === '-') return;
      const button = this.querySelector(`[data-step="${name}"] [data-answer="${CSS.escape(value)}"]`);
      if (!button) return;
      this.answers[name] = value;
      this.labels[name] = button.querySelector('[data-answer-label]')?.textContent.trim() || '';
      this.#markPressed(name);
    });
    return Object.keys(this.answers).length > 0;
  }

  #readUrls() {
    try {
      return JSON.parse(this.querySelector('script[data-quiz-urls]')?.textContent || '{}');
    } catch {
      return {};
    }
  }
}

if (!customElements.get('s-form-quiz')) customElements.define('s-form-quiz', SFormQuiz);
